'use client';

import { c as _c } from "react/compiler-runtime";
import { type Post } from '@shared/all/domains/posts';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronRight, History } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Button from '../../components/common/Button';
import GoBackButton from '../../components/factiiis/general/GoBackButton';
import CardActivity from '../../components/pages/space/CardActivity';
import PostItem from '../../components/posts/PostItem';
import RepliesContainer from '../../components/posts/RepliesContainer';
import AppLayout from '../../layouts/AppLayout/AppLayout';
import { contentToPlainText, truncateText } from '../../utils/contentText';
import { client, type RouterOutputs } from '../../utils/trpc';

// Body excerpt length for the sr-only heading of an untitled post.
const HEADING_SNIPPET_MAX = 120;
type PostPageProps = {
  initialData?: RouterOutputs['posts']['singlePost'];
  postId: string;
  initialError?: boolean;
  isEmbed?: boolean;
};
const PostPage = t0 => {
  const $ = _c(51);
  const {
    initialData,
    postId,
    initialError,
    isEmbed: t1
  } = t0;
  const isEmbed = t1 === undefined ? false : t1;
  const router = useRouter();
  const {
    queryKey
  } = usePostsShared(client);
  const {
    isPolitics,
    isNsfw
  } = usePostsShared(client);
  let t2;
  if ($[0] !== postId) {
    t2 = async () => {
      const [error, result] = await client.posts.singlePost.query({
        postId
      });
      if (error) {
        throw error;
      }
      return result;
    };
    $[0] = postId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = Boolean(postId) && !initialError;
  const t4 = !initialData;
  let t5;
  if ($[2] !== initialData) {
    t5 = initialData ? {
      pages: [initialData],
      pageParams: [0]
    } : undefined;
    $[2] = initialData;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== queryKey || $[5] !== t2 || $[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t6 = {
      queryKey,
      queryFn: t2,
      initialPageParam: 0,
      getNextPageParam: _temp,
      enabled: t3,
      refetchOnMount: t4,
      initialData: t5,
      refetchOnWindowFocus: false,
      staleTime: 300000
    };
    $[4] = queryKey;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data
  } = useInfiniteQuery(t6);
  const renderData = data;
  const renderPostDetails = renderData?.pages[0]?.posts[0];
  const renderParentPost = renderPostDetails?.parentPost || renderPostDetails;
  let t7;
  if ($[10] !== renderPostDetails) {
    t7 = renderPostDetails?.parentPost ? {
      ...renderPostDetails.parentPost,
      replies: [renderPostDetails]
    } : renderPostDetails;
    $[10] = renderPostDetails;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const renderThread = t7;
  const postSpaceSlug = renderParentPost?.space ?? renderPostDetails?.space;
  const isBoardPost = !!renderPostDetails?.pos;
  const postAuthor = renderPostDetails?.user?.username;
  let t8;
  if ($[12] !== renderPostDetails?.title) {
    t8 = renderPostDetails?.title?.trim();
    $[12] = renderPostDetails?.title;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const postTitle = t8;
  let t9;
  if ($[14] !== renderPostDetails?.content) {
    t9 = truncateText(contentToPlainText(renderPostDetails?.content), HEADING_SNIPPET_MAX);
    $[14] = renderPostDetails?.content;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const postSnippet = t9;
  const untitledHeading = postAuthor ? `Post by @${postAuthor}${postSnippet ? `: ${postSnippet}` : ""}` : "Post on Factiii";
  const postHeading = postTitle || untitledHeading;
  let t10;
  if ($[16] !== isBoardPost || $[17] !== isEmbed) {
    t10 = isEmbed || !isBoardPost ? undefined : {
      items: []
    };
    $[16] = isBoardPost;
    $[17] = isEmbed;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== isBoardPost || $[20] !== isEmbed || $[21] !== postId) {
    t11 = isEmbed || !isBoardPost ? undefined : {
      items: [{
        icon: <History />,
        text: "Activity",
        component: <CardActivity postId={postId} />
      }]
    };
    $[19] = isBoardPost;
    $[20] = isEmbed;
    $[21] = postId;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== postHeading || $[24] !== renderPostDetails) {
    t12 = renderPostDetails && <h1 className="sr-only">{postHeading}</h1>;
    $[23] = postHeading;
    $[24] = renderPostDetails;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <GoBackButton text="Home" />;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== postSpaceSlug) {
    t14 = postSpaceSlug && <Link href={`/s/${postSpaceSlug}`} className="flex items-center gap-1 text-sm font-emphasis text-textPrimary/60 hover:text-textSecondary/70"><span>Go to s/{postSpaceSlug}</span><ChevronRight size={16} className="text-current" /></Link>;
    $[27] = postSpaceSlug;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t12 || $[30] !== t14) {
    t15 = <div className="flex items-center justify-between">{t12}{t13}{t14}</div>;
    $[29] = t12;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== isBoardPost || $[33] !== isNsfw || $[34] !== isPolitics || $[35] !== renderParentPost || $[36] !== renderPostDetails || $[37] !== renderThread) {
    t16 = renderParentPost && renderPostDetails && <div><PostItem disableLink={true} isFullLength={true} isInView={true} post={renderParentPost} isThread={Boolean(renderPostDetails.parentPostId)} />{isBoardPost && <div className="mt-4 rounded-lg bg-background-secondary p-4 shadow-sm lg:hidden"><h3 className="mb-3 text-sm font-emphasis text-textPrimary">Activity</h3><CardActivity postId={renderPostDetails.id} /></div>}{renderThread && <RepliesContainer disableLink={false} isFullLength={true} isPoliticsParent={isPolitics(renderThread.postFactiiisCount, renderThread.space)} isNsfwParent={isNsfw(renderThread.postFactiiisCount)} post={renderThread as Post} threadId={renderPostDetails.parentThreadId} />}</div>;
    $[32] = isBoardPost;
    $[33] = isNsfw;
    $[34] = isPolitics;
    $[35] = renderParentPost;
    $[36] = renderPostDetails;
    $[37] = renderThread;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== initialError || $[40] !== router) {
    t17 = initialError && <div className="mt-4 flex size-full flex-col items-center justify-center rounded-md bg-background-secondary py-10 shadow-md"><p className="mb-4 text-lg font-semibold">Post Not Found</p><Button text="Go Home" onClick={() => router.push("/")} classNames="hover:border-2 hover:border-ds-border-standard" /></div>;
    $[39] = initialError;
    $[40] = router;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t15 || $[43] !== t16 || $[44] !== t17) {
    t18 = <section className="space-y-4">{t15}{t16}{t17}</section>;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== isEmbed || $[47] !== t10 || $[48] !== t11 || $[49] !== t18) {
    t19 = <AppLayout isEmbed={isEmbed} leftSidebar={t10} rightSidebar={t11}>{t18}</AppLayout>;
    $[46] = isEmbed;
    $[47] = t10;
    $[48] = t11;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
};
export default PostPage;
function _temp() {}